import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { DYNAMITE_ORB_STATES } from '../config/dynamite';

/**
 * DynaMite's launcher as an orb — G2, lit teal glass (DWEB-146, v6.6.1).
 *
 * PORTED FROM THE REFERENCE IMPLEMENTATION, NOT RE-DERIVED. The gradients,
 * radii, masks, timings and easings below are the prototype's
 * (~/workspace/DynamWorks-ClaudeCoWork/DW-website/_orb/dynamite-orb-prototype.html),
 * which the owner approved after five render sheets. v6.6.0's orb measured
 * 1.73 Weber contrast against the site's midnight-blue ground on its first
 * redesign and read as blue-on-blue; G2 measures 2.52 on the same sheet, and
 * its glow makes the eye's gap larger than the number. Where the site's
 * styled-components idiom differs from the prototype's plain CSS the numbers
 * are kept and only the syntax changes. Change a number here and the owner
 * gets a sixth sheet.
 *
 * THE SPHERE IS 60px INSIDE A 120px BUTTON THAT PAINTS NOTHING (DWEB-149,
 * v6.6.2; 44px in 80 on a phone). v6.6.1's orb filled the launcher's box,
 * and the owner found it too big: the old robot head — SphereGeometry(1)
 * at scale 0.3, camera z -1.5, fov 45, on a 120px canvas — rendered its
 * sphere at ~59px, half the launcher, and that is the size he asked for.
 * The prototype (updated 2026-09-06, `orb-size.png` the approved sheet)
 * draws exactly this: the button is the HIT TARGET only — same box, same
 * name, same test id, same data-state; no background, border, shadow or
 * backdrop-filter of its own — and every optical layer lives on a child
 * `.sphere` centred in it. Three things follow, each in the prototype:
 * orbCoreCenter() measures the SPHERE (the pull's origin and the morph's
 * travelling core both start there); the pull's maximum scales with the
 * sphere (diameter / 12: 5px at 60, 10px at 120; the radius stays 240px);
 * and the glow radii are in units of the diameter (`--d`), so a 60px orb
 * does not carry a 120px halo. The DWEB-142 bubble keeps measuring the
 * button's box and sits a little further from the visible sphere — by
 * decision, not moved.
 *
 * SIX LAYERS, ONE OF WHICH MOVES. The sphere is the glass itself: a
 * backdrop-filter with brightness(1.35) — the LIT SHELL, because blur alone
 * blurs dark into dark — over a teal/cyan/blue interior. Inside it, six
 * absolutely positioned <i> layers: the shell (bright top-left), the mass
 * (brand blue and cyan), the CORE (the two-stage amber resultant, the only
 * layer the pointer moves), the CAUSTIC (the warm crescent on the lower-right
 * inner edge), the FRESNEL RIM (bright all round, brightest where the light
 * hits) and the specular. The caustic and the rim are shaped by mask-image;
 * a mask does not promote a compositor layer on its own, which the release's
 * layer census confirmed against the v6.6.0 build. Moving the backdrop from
 * the button to the sphere moves one layer, it does not add one.
 *
 * NO `will-change`, NO `translateZ(0)`. LiquidGlassStyles.js carries both on
 * every card; DWEB-86 counts the layers they promote (41 permanent in the
 * 2026-08-28 reading, 43 by this release's instrument), and this element
 * sits above everything else on the page. The optics, not the compositor
 * hints.
 *
 * THE PULL. `pointermove` on the window writes two CSS variables on the core
 * layer and a .42s transition on its `transform` does the rest: radius
 * 240px, and at most the SPHERE's diameter / 12 of travel (5px at 60px),
 * the strength growing as the pointer approaches, like a force. Leaving the
 * radius settles it to zero. `touchstart` on the orb pulls once on a phone.
 * It applies to the orb face ONLY and is off while the panel is open — the
 * effect below unsubscribes on `isOpen`.
 *
 * NO requestAnimationFrame. Not for throttling, not for anything. An event
 * handler that writes a style is not a loop; a rAF "throttle" of the same
 * handler IS one, and it is the DWEB-83 defect (zero rAF callers five
 * seconds after load, e2e/support/rafCensus.js) in a new coat.
 * dynamite-orb.spec.js samples the census DURING a run of pointer moves
 * and expects zero.
 *
 * THE ORB BECOMES THE PANEL (DynaMiteWidget.js) — AND STAYS IN THE CORNER,
 * EMPTIED (DWEB-150, v6.6.3). On open the panel is revealed with a clip-path
 * circle growing from this element's center and the CORE travels to the
 * panel's header, where it becomes the state indicator (DynamiteCore below).
 * Until v6.6.2 the whole orb faded to zero with it; now ONLY the core layer
 * is hidden (`visibility`, no transition — the travelling core appears at
 * the same spot in the same frame) and the shell, mass, caustic, rim and
 * specular stay at opacity 1: the point of light has moved into the
 * conversation and the sphere it left is still there. On close the core
 * flies back (the widget's return flight) and the layer reappears when it
 * lands. The pull stays off while open; the launcher keeps its pointer and
 * its tab stop, because it is still the close control. Its box never
 * changes, so the backdrop-filter never re-rasterises a new size.
 *
 * SPEAKING IS A STATE, NOT AN ANIMATION. The state model
 * (DYNAMITE_ORB_STATES: idle, listening, thinking, speaking) lives on the
 * HEADER core as `data-state`; keyframes apply only while the attribute
 * holds and stop on idle — listening breathes the halo, thinking drifts the
 * point, speaking pulses the pin. All four are wired since v6.6.2
 * (DWEB-147): DynaMiteWidget derives the state from the TEXT turn —
 * listening while the composer has focus, thinking from send to the first
 * streamed part, speaking between `start` and `stop` — and passes the same
 * value to the header core and to the orb face's `data-state`. DWEB-130
 * (voice, v6.6.3) drives the same four from `onModeChange`. Whoever touches
 * it: set `state`, do not add a requestAnimationFrame. The turn's motion
 * belongs to the turn and ends with it; a loop that outlives the turn is the
 * DWEB-83 defect, and mainthread.spec.js will catch it.
 *
 * THE SPHERE FLOATS (DWEB-151, v6.6.4) — THE OWNER'S CALL, ON v6.6.3 LIVE:
 * "the orb or the orb launcher is not bouncing like before. appears too
 * static. the synced avatar orb bg bounces." "Still at rest" was a
 * recommendation made against DWEB-83, and the float never touched DWEB-83:
 * DynamiteAvatar.js has run `float 6s ease-in-out infinite` (translateY
 * -15px, rotate 3deg) since v6.5.1 while the rAF census read zero every
 * release — a compositor transform animation is invisible to it. The cost
 * is ONE permanently animating layer (48 -> 49 at rest, declared on the
 * ticket), which the avatar face already pays. So: the SAME keyframes, on
 * the SPHERE, not the button — the 60px orb drifts and the 120px hit target
 * stays put (better than the avatar face, whose whole button moves; the
 * DWEB-142 bubble measures the button, which does not move). Same timing
 * as the head — 6s, 3deg — and the amplitude in units of the diameter:
 * `--d * -0.25` is the head's 15px at 60 and 11px on a 44px phone sphere,
 * where 15 would be a third of the ball. Paused on hover, as the head's is,
 * so the pull has a still target under the pointer. It keeps floating while
 * the panel is open — the emptied shell waits; the head does the same. The
 * pull's orbCoreCenter() reads the sphere's live rect, which now moves; the
 * core is a child and drifts with it, so the pull is measured from where
 * the core is. The core does NOT float on its own: speaking is a state, not
 * an animation — the float is the body, the resultant's motion stays
 * state-driven.
 *
 * prefers-reduced-motion: under `reduce` nothing here animates in any
 * state — the float and the state keyframes sit inside
 * `(prefers-reduced-motion: no-preference)` and the core's pull transition
 * is `none` — over and above index.css's blanket rule. A practice that
 * sells regulated delivery does not ignore that media query.
 */

/** The launcher's HIT TARGET, desktop and phone — the button's box. DynaMiteWidget's reveal anchors on these; the sphere is centred in it. */
export const ORB_SIZE = 120;
export const ORB_SIZE_PHONE = 80;
/** The SPHERE's diameter, desktop and phone (DWEB-149): the old robot head's ~59px, and the prototype's 60 / 44. */
export const ORB_SPHERE = 60;
export const ORB_SPHERE_PHONE = 44;
/** Where the core sits inside the sphere, as fractions of its box (the prototype's 61% / 66%). */
export const ORB_CORE_AT = { x: 0.61, y: 0.66 };
/** The pull's radius, in px, fixed; its maximum is the sphere's diameter divided by this (5px at 60). */
const PULL_RADIUS = 240;
const PULL_MAX_DIVISOR = 12;

/** The sphere inside a launcher element (the button), or the element itself if it has none. */
function sphereOf(el) {
  return (el && el.querySelector('[data-testid="dynamite-orb-sphere"]')) || el;
}

/**
 * The core's center on the page, for the travelling core (DynaMiteWidget)
 * and the pull. Measured from the SPHERE's current position (DWEB-149), so
 * it is right at 44px in 80 too. `size` is the sphere's diameter — its
 * LAYOUT width, not its bounding box: the float (DWEB-151) rotates the
 * sphere by up to 3deg, and the bounding box of a rotated 60px circle reads
 * 63, which put the pull's maximum at 5.07 instead of 5.00 on the first run
 * (the spec caught it). The rect's midpoint is the sphere's true center
 * whatever the rotation — the button's center plus the float's offset — and
 * the core sits at a fixed fraction of the layout size from it.
 */
export function orbCoreCenter(el) {
  const sphere = sphereOf(el);
  const r = sphere.getBoundingClientRect();
  const size = sphere.offsetWidth || r.width;
  return {
    x: r.left + r.width / 2 + (ORB_CORE_AT.x - 0.5) * size,
    y: r.top + r.height / 2 + (ORB_CORE_AT.y - 0.5) * size,
    size,
  };
}

/* ------------------------------------------------------------------ */
/* The orb face                                                        */
/* ------------------------------------------------------------------ */

/*
 * The hit target (DWEB-149): the prototype's `.orb`. It paints NOTHING —
 * transparent, no border, no shadow, no backdrop — and centres the sphere.
 * `--hit` and `--d` are the prototype's two variables; the sphere's shadow
 * radii are written in units of `--d`.
 */
const Hit = styled.button`
  --hit: ${ORB_SIZE}px;
  --d: ${ORB_SPHERE}px;
  pointer-events: auto;
  position: relative;
  flex-shrink: 0;
  width: var(--hit);
  height: var(--hit);
  padding: 0;
  margin: 0;
  border: 0;
  background: transparent;
  box-shadow: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: none;

  &:focus-visible {
    outline: none;
  }

  @media (max-width: 768px) {
    --hit: ${ORB_SIZE_PHONE}px;
    --d: ${ORB_SPHERE_PHONE}px;
  }
`;

/*
 * The float (DWEB-151): DynamiteAvatar.js's keyframes with the amplitude in
 * units of the sphere's diameter — a custom property resolves against the
 * animated element, so one keyframe serves 60 and 44.
 */
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(calc(var(--d) * -0.25)) rotate(3deg); }
`;

/*
 * The sphere: the glass itself (the prototype's `.sphere`). The ONE
 * backdrop-filter on the launcher, the top-lit border, the depth ramp, and
 * the glow in units of the diameter. Its LAYOUT box never changes: on open
 * only the core layer hides, the sphere is not resized; the float moves its
 * painted box, never its layout box.
 */
const Sphere = styled.span`
  position: relative;
  display: block;
  width: var(--d);
  height: var(--d);
  border-radius: 50%;
  overflow: hidden;
  animation: none;

  /* DWEB-151: the float — the head's 6s drift, on the sphere, under no-preference only; paused under the pointer so the pull has a still target. */
  @media (prefers-reduced-motion: no-preference) {
    animation: ${float} 6s ease-in-out infinite;
  }

  ${Hit}:hover & {
    animation-play-state: paused;
  }
  border: 1.5px solid rgba(255, 255, 255, 0.55);
  border-top-color: rgba(255, 255, 255, 0.95);
  border-bottom-color: rgba(255, 255, 255, 0.28);
  -webkit-backdrop-filter: blur(20px) saturate(240%) brightness(1.35);
  backdrop-filter: blur(20px) saturate(240%) brightness(1.35);
  background: radial-gradient(circle at 50% 50%, rgba(34, 211, 238, 0.42) 0%, rgba(20, 184, 166, 0.5) 48%, rgba(30, 64, 175, 0.66) 100%);
  box-shadow:
    0 calc(var(--d) * 0.3) calc(var(--d) * 0.73) rgba(0, 0, 0, 0.48),
    0 0 calc(var(--d) * 0.73) rgba(245, 158, 11, 0.3),
    0 0 calc(var(--d) * 1.5) rgba(59, 130, 246, 0.28),
    inset 0 0 calc(var(--d) * 0.5) rgba(255, 255, 255, 0.12);
  transition: box-shadow 0.25s ease;

  ${Hit}:focus-visible & {
    box-shadow: 0 0 0 3px #fbbf24, 0 calc(var(--d) * 0.3) calc(var(--d) * 0.73) rgba(0, 0, 0, 0.48);
  }
`;

const Layer = styled.i`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  pointer-events: none;
  display: block;
`;

/* lit shell: bright top-left, clear through the middle, faintly dark at the bottom */
const Shell = styled(Layer)`
  background:
    radial-gradient(110% 110% at 22% 14%, rgba(255, 255, 255, 0.62) 0%, rgba(255, 255, 255, 0.22) 28%, rgba(255, 255, 255, 0.04) 52%, rgba(255, 255, 255, 0) 66%),
    linear-gradient(160deg, rgba(255, 255, 255, 0) 60%, rgba(0, 0, 0, 0.1) 100%);
`;

/* interior colour: brand blue and cyan */
const Mass = styled(Layer)`
  background:
    radial-gradient(60% 42% at 36% 40%, rgba(147, 197, 253, 0.9) 0%, rgba(147, 197, 253, 0) 78%),
    radial-gradient(50% 36% at 68% 30%, rgba(34, 211, 238, 0.72) 0%, rgba(34, 211, 238, 0) 82%);
`;

/* the resultant. The ONLY layer the pointer moves — and the only layer
   hidden while the panel is open (DWEB-150): `visibility`, which no
   transition touches, so the pull's `transform 0.42s` stays the one
   transition on this layer. */
const Core = styled(Layer)`
  --px: 0px;
  --py: 0px;
  visibility: ${(p) => (p.$hidden ? 'hidden' : 'visible')};
  transform: translate(var(--px), var(--py));
  transition: transform 0.42s cubic-bezier(0.2, 0.8, 0.2, 1);
  background:
    radial-gradient(4% 4% at 61% 66%, rgba(255, 255, 255, 1) 0%, rgba(255, 250, 230, 1) 70%, rgba(251, 191, 36, 0) 100%),
    radial-gradient(12% 12% at 61% 66%, rgba(253, 224, 71, 0.98) 0%, rgba(245, 158, 11, 0.55) 60%, rgba(245, 158, 11, 0) 100%),
    radial-gradient(34% 34% at 61% 66%, rgba(245, 158, 11, 0.48) 0%, rgba(245, 158, 11, 0) 100%);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/* caustic: light concentrating on the far inner edge */
const Caustic = styled(Layer)`
  background: radial-gradient(circle at 50% 50%, rgba(255, 236, 190, 0) 70%, rgba(255, 236, 190, 0.55) 81%, rgba(255, 248, 225, 0.95) 87%, rgba(255, 255, 255, 0) 94%);
  -webkit-mask-image: linear-gradient(135deg, transparent 42%, #000 72%);
  mask-image: linear-gradient(135deg, transparent 42%, #000 72%);
`;

/* fresnel rim: bright all round, brightest where the light hits */
const Rim = styled(Layer)`
  background: radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0) 76%, rgba(255, 255, 255, 0.28) 90%, rgba(255, 255, 255, 0.85) 100%);
  -webkit-mask-image: linear-gradient(315deg, rgba(0, 0, 0, 0.35) 20%, #000 75%);
  mask-image: linear-gradient(315deg, rgba(0, 0, 0, 0.35) 20%, #000 75%);
`;

/* the specular, in percent of the sphere (the prototype's 9% x 6.5% and 18% x 5%: 11px x 8px and 22px x 6px at 120) */
const Spec = styled(Layer)`
  background:
    radial-gradient(9% 6.5% at 30% 22%, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%),
    radial-gradient(18% 5% at 36% 30%, rgba(255, 255, 255, 0.35) 0%, rgba(255, 255, 255, 0) 100%);
`;

/**
 * The launcher. Keeps the avatar launcher's contract exactly: a button named
 * "Open DynaMite chat" / "Close DynaMite chat", `aria-expanded`, the
 * `dynamite-launcher` test id, `onClick` toggling the panel. `data-launcher`
 * says which face this is, for the suite; `data-state` mirrors the header
 * core's state (inert here — no state adds a keyframe to the orb face; the
 * sphere's float, DWEB-151, runs the same in every state).
 * Since DWEB-149 the button is the invisible hit target and the sphere is
 * a child of it; the ref handed out is still the button.
 *
 * While the panel is open the orb has become the panel — its core has
 * left for the header — and the emptied sphere stays in the corner as the
 * close control (DWEB-150): it keeps its pointer, its tab stop and its
 * place in the accessibility tree as the expanded control it is. Keyboard
 * focus stays here after the click, which is what lets Escape, handled on
 * the surface's root, close the panel. `coreHidden` is the widget's word on
 * the core layer: hidden from the click that opens until the return flight
 * lands; it defaults to `isOpen`.
 */
const DynamiteOrb = forwardRef(function DynamiteOrb({ onClick, isOpen = false, state = 'idle', coreHidden }, ref) {
  const hideCore = typeof coreHidden === 'boolean' ? coreHidden : isOpen;
  const orbState = DYNAMITE_ORB_STATES.includes(state) ? state : 'idle';
  const elRef = useRef(null);
  const coreRef = useRef(null);
  useImperativeHandle(ref, () => elRef.current);

  useEffect(() => {
    const el = elRef.current;
    const core = coreRef.current;
    if (!el || !core) return undefined;
    const settle = () => {
      core.style.setProperty('--px', '0px');
      core.style.setProperty('--py', '0px');
    };
    if (isOpen) {
      settle();
      return undefined;
    }
    const pull = (x, y) => {
      // DWEB-149: measured from the SPHERE; the maximum is its diameter / 12.
      const c = orbCoreCenter(el);
      if (!c.size) return;
      const dx = x - c.x;
      const dy = y - c.y;
      const d = Math.hypot(dx, dy);
      if (d > PULL_RADIUS || d === 0) {
        settle();
        return;
      }
      const k = (c.size / PULL_MAX_DIVISOR) * (1 - d / PULL_RADIUS); // closer = stronger, like a force
      core.style.setProperty('--px', `${((dx / d) * k).toFixed(2)}px`);
      core.style.setProperty('--py', `${((dy / d) * k).toFixed(2)}px`);
    };
    const onMove = (ev) => pull(ev.clientX, ev.clientY);
    const onTouch = (ev) => {
      const t = ev.touches && ev.touches[0];
      if (t) pull(t.clientX, t.clientY);
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('pointerleave', settle);
    el.addEventListener('touchstart', onTouch, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerleave', settle);
      el.removeEventListener('touchstart', onTouch);
      settle();
    };
  }, [isOpen]);

  return (
    <Hit
      ref={elRef}
      type="button"
      onClick={onClick}
      aria-label={`${isOpen ? 'Close' : 'Open'} DynaMite chat`}
      aria-expanded={isOpen}
      data-testid="dynamite-launcher"
      data-launcher="orb"
      data-state={orbState}
      data-core-hidden={hideCore ? 'true' : undefined}
    >
      <Sphere data-testid="dynamite-orb-sphere" aria-hidden="true">
        <Shell data-orb-layer="shell" />
        <Mass data-orb-layer="mass" />
        <Core ref={coreRef} data-orb-layer="core" data-testid="dynamite-orb-core" $hidden={hideCore} />
        <Caustic data-orb-layer="caustic" />
        <Rim data-orb-layer="rim" />
        <Spec data-orb-layer="spec" />
      </Sphere>
    </Hit>
  );
});

/* ------------------------------------------------------------------ */
/* The header core — the orb's core, arrived. Motion ONLY via data-state */
/* ------------------------------------------------------------------ */

const breathe = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.55; }
  50% { transform: scale(1.45); opacity: 0.95; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.22); }
`;

const resolve = keyframes`
  0%, 100% { transform: translate(0, 0); }
  25% { transform: translate(2px, -2px); }
  50% { transform: translate(-2px, 1px); }
  75% { transform: translate(1px, 2px); }
`;

/*
 * The conversation states. Applied ONLY by data-state, ONLY when the
 * visitor has not asked for reduced motion; transform and opacity only (the
 * compositor, not the main thread — DWEB-83's census counts rAF callers and
 * cannot see these, and DWEB-25's census allows transform/opacity). In
 * `idle` none of these selectors match and nothing animates.
 */
const conversation = css`
  @media (prefers-reduced-motion: no-preference) {
    &[data-state='listening']::after {
      animation: ${breathe} 2.2s ease-in-out infinite;
    }
    &[data-state='thinking'] {
      animation: ${resolve} 2.6s ease-in-out infinite;
    }
    &[data-state='speaking']::before {
      animation: ${pulse} 0.62s ease-in-out infinite;
    }
    &[data-state='speaking']::after {
      animation: ${breathe} 0.62s ease-in-out infinite;
    }
  }
`;

const CoreDot = styled.div`
  position: relative;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  flex-shrink: 0;
  animation: none;
  opacity: ${(p) => (p.$hidden ? 0 : 1)};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: radial-gradient(circle at 50% 50%, #fffdf6 0%, #fff8dc 22%, rgba(251, 191, 36, 0.96) 42%, rgba(245, 158, 11, 0.25) 70%, rgba(245, 158, 11, 0) 100%);
  }

  &::after {
    content: '';
    position: absolute;
    inset: -8px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(245, 158, 11, 0.38) 0%, rgba(245, 158, 11, 0) 70%);
    opacity: 0.7;
  }

  ${conversation}
`;

/**
 * The state indicator in the panel's header, whichever face launched the
 * panel. `hidden` is true only while the travelling core is in flight from
 * the orb (DynaMiteWidget), so the two are never on screen at once.
 */
export function DynamiteCore({ state = 'idle', hidden = false }) {
  const coreState = DYNAMITE_ORB_STATES.includes(state) ? state : 'idle';
  return <CoreDot data-state={coreState} data-testid="dynamite-core" aria-hidden="true" $hidden={hidden} />;
}

export default DynamiteOrb;
